import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import {
  type FacilityTransferItemPublicRecord,
  type FacilityTransferRoundPublicRecord,
  FacilityTransferDirection,
  WheelExternalCheckStatus
} from '@navio/nsdk';
import { NoddiBasicCard } from 'noddi-ui-web';
import { PickupRoundGroups } from './PickupRoundGroups';
import { StatusSection } from './StatusSection';

export type StatusFilter = 'ALL' | WheelExternalCheckStatus;

interface Props {
  items: FacilityTransferItemPublicRecord[];
  rounds: FacilityTransferRoundPublicRecord[];
  statusFilter: StatusFilter;
  isSearching: boolean;
}

export const WheelSetList = ({ items, rounds, statusFilter, isSearching }: Props) => {
  if (items.length === 0) {
    return (
      <NoddiBasicCard>
        <p className='text-sm text-muted-foreground'>
          <Trans>No wheel sets match the current filters.</Trans>
        </p>
      </NoddiBasicCard>
    );
  }

  if (statusFilter !== 'ALL') {
    return <PickupRoundGroups items={items} rounds={rounds} direction={FacilityTransferDirection.OUTGOING} />;
  }

  return (
    <div className='flex flex-col gap-6'>
      {STATUS_SECTION_ORDER.map((status) => {
        const sectionItems = items.filter((item) => item.externalCheckStatus === status);
        if (sectionItems.length === 0) {
          return null;
        }
        return (
          <StatusSection
            key={status}
            title={getStatusSectionTitle(status)}
            items={sectionItems}
            rounds={rounds}
            isCollapsible={status === WheelExternalCheckStatus.READY}
            isForcedOpen={isSearching}
          />
        );
      })}
    </div>
  );
};

// Wheel sets still waiting for an answer come first; they are what the recipient has left to do.
const STATUS_SECTION_ORDER: WheelExternalCheckStatus[] = [
  WheelExternalCheckStatus.PENDING,
  WheelExternalCheckStatus.ISSUE,
  WheelExternalCheckStatus.READY
];

const getStatusSectionTitle = (status: WheelExternalCheckStatus): string => {
  switch (status) {
    case WheelExternalCheckStatus.PENDING:
      return t`Needs an answer`;
    case WheelExternalCheckStatus.ISSUE:
      return t`Issue reported`;
    case WheelExternalCheckStatus.READY:
      return t`Marked ready for pickup`;
  }
};
